/**
 * useFeedQuery — TanStack Query over the family feed + the reaction toggle.
 *
 * Backend (Fase 5 feed.routes):
 *   - GET  /api/feed            → FeedItemView[] (newest-first, actor-enriched,
 *                                 reaction tallies + the viewer's own reaction)
 *   - POST /api/feed/:id/react  → { emoji } (toggle: tap active clears, a
 *                                 different one replaces)
 *
 * The reaction mutation is OPTIMISTIC — tapping an emoji updates the cached
 * counts + my_reaction immediately, rolls back on error, and resyncs on
 * settle. useFeedWS additionally invalidates ['feed'] when another member
 * reacts, so every client converges on the server-authoritative tallies.
 *
 * Cache: staleTime 30s mirrors the chore/points feeds — WS invalidation keeps
 * it live, the stale window is just a polling backstop.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FEED_REACTIONS, type FeedItemKind, type FeedReaction } from '@stell/shared';
import { toast } from '../../lib/toast';

export interface FeedReactionCount {
  emoji: FeedReaction;
  count: number;
}

export interface FeedItemView {
  id: string;
  kind: FeedItemKind;
  actor_id: string;
  actor_short_name: string;
  actor_color_token: string | null;
  actor_avatar_url: string | null;
  title: string;
  points: number | null;
  ref_kind: string | null;
  ref_id: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
  reactions: FeedReactionCount[];
  my_reaction: FeedReaction | null;
}

export const FEED_KEY = ['feed'] as const;

interface FetchError extends Error {
  status: number;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const err = new Error(`fetch_failed:${r.status}`) as FetchError;
    err.status = r.status;
    throw err;
  }
  return (await r.json()) as T;
}

export function useFeed() {
  return useQuery<FeedItemView[]>({
    queryKey: FEED_KEY,
    queryFn: () => fetchJson<FeedItemView[]>('/api/feed'),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Apply the toggle semantics to one item's view, returning a fresh copy with
 * recomputed counts (fixed-set order preserved) and my_reaction. Pure — used
 * for the optimistic cache patch.
 */
export function applyReactionToggle(item: FeedItemView, emoji: FeedReaction): FeedItemView {
  const counts = new Map<FeedReaction, number>();
  for (const r of item.reactions) counts.set(r.emoji, r.count);
  const prev = item.my_reaction;

  let next: FeedReaction | null;
  if (prev === emoji) {
    // Tap the active emoji → clear.
    counts.set(emoji, (counts.get(emoji) ?? 1) - 1);
    next = null;
  } else {
    if (prev) counts.set(prev, (counts.get(prev) ?? 1) - 1);
    counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
    next = emoji;
  }

  const reactions: FeedReactionCount[] = FEED_REACTIONS.filter(
    (e) => (counts.get(e) ?? 0) > 0,
  ).map((e) => ({ emoji: e, count: counts.get(e)! }));

  return { ...item, reactions, my_reaction: next };
}

async function postReact(itemId: string, emoji: FeedReaction): Promise<void> {
  const r = await globalThis.fetch(`/api/feed/${itemId}/react`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!r.ok) {
    const err = new Error(`fetch_failed:${r.status}`) as FetchError;
    err.status = r.status;
    throw err;
  }
}

export function useReactToFeed() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation<
    void,
    FetchError,
    { itemId: string; emoji: FeedReaction },
    { prev: FeedItemView[] | undefined }
  >({
    mutationFn: ({ itemId, emoji }) => postReact(itemId, emoji),
    onMutate: async ({ itemId, emoji }) => {
      await qc.cancelQueries({ queryKey: FEED_KEY });
      const prev = qc.getQueryData<FeedItemView[]>(FEED_KEY);
      qc.setQueryData<FeedItemView[]>(FEED_KEY, (old) =>
        old?.map((it) => (it.id === itemId ? applyReactionToggle(it, emoji) : it)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData<FeedItemView[]>(FEED_KEY, ctx.prev);
      toast({ kind: 'error', body: t('feed.reactError') });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: FEED_KEY });
    },
  });
}
